import {
  ClientWorkFlowStatus,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { PayableBulkActionType } from "@wingspanhq/wingspan-api-types/dist/src/generated/paymentsApiSpec";
import { WSQueryCache } from "@ws-react-query";
import {
  QUERY_PAYABLES,
  QUERY_PAYABLES_SUMMARY,
  QUERY_PAYABLES_WITH_PAYEES
} from "../../../query/payables/keys";
import {
  QUERY_PAYABLE,
  QUERY_PAYABLES as QUERY_PAYABLES_LEGACY
} from "../../../query/payments/keys";
import { Payable } from "../../../services/api/payments/payables";
import { paymentsService } from "../../../services/payments";

export const invalidatePayablesAfterMutation = (successItems: Payable[]) => {
  WSQueryCache.invalidateQueries(QUERY_PAYABLES);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_WITH_PAYEES);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_LEGACY);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_SUMMARY);
  successItems.forEach(payable => {
    if (payable.payableId) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.payableId]);
    }
  });
};

const pluralizePayable = (count: number) => `payable${count === 1 ? "" : "s"}`;

const requirePayableId = (payable: Payable): string => {
  if (!payable.payableId) {
    throw new Error("Payable is missing payableId");
  }
  return payable.payableId;
};

type PayableBulkActionConfig = {
  /** Header of the featured action card. */
  cardHeader: string;
  /** Action button label and modal title. */
  actionName: string;
  confirmLabel: string;
  confirmDescription: (count: number) => string;
  confirmDestructive?: boolean;
  /**
   * Items the action can't apply to are skipped (counted as processed, not
   * failed) rather than erroring the whole run — e.g. an already-paid payable
   * can't be approved.
   */
  isApplicable?: (payable: Payable) => boolean;
  /** Per-item mutation. Throwing marks that single item as failed. */
  applyToItem: (payable: Payable) => Promise<unknown>;
};

/**
 * Single source of truth for the payable bulk actions the backend exposes
 * (`PayableBulkActionType`). Drives both the featured-action card copy and the
 * FE per-item processing used below the batch-API threshold.
 */
export const PAYABLE_BULK_ACTION_CONFIG: Record<
  PayableBulkActionType,
  PayableBulkActionConfig
> = {
  Approve: {
    cardHeader: "Pending approval",
    actionName: "Approve payables",
    confirmLabel: "Approve",
    confirmDescription: count =>
      `Approve ${pluralizePayable(count)} to be paid`,
    isApplicable: payable => payable.status !== "Paid",
    applyToItem: payable =>
      paymentsService.payable.update(requirePayableId(payable), {
        client: { workflowStatus: ClientWorkFlowStatus.Approved }
      })
  },
  PreApprove: {
    cardHeader: "Pending pre-approval",
    actionName: "Pre-approve payables",
    confirmLabel: "Pre-approve",
    confirmDescription: count =>
      `Pre-approve ${pluralizePayable(count)} to be approved`,
    applyToItem: payable =>
      paymentsService.payable.update(requirePayableId(payable), {
        client: { workflowStatus: ClientWorkFlowStatus.PreApproved }
      })
  },
  Unapprove: {
    cardHeader: "Pending unapproval",
    actionName: "Unapprove payables",
    confirmLabel: "Unapprove",
    confirmDescription: () => "Unapprove payables from being paid",
    isApplicable: payable => payable.status !== "Paid",
    applyToItem: payable =>
      paymentsService.payable.update(requirePayableId(payable), {
        client: { workflowStatus: ClientWorkFlowStatus.Pending }
      })
  },
  Open: {
    cardHeader: "Draft payables",
    actionName: "Open payables",
    confirmLabel: "Open",
    confirmDescription: () => "Open draft payables",
    isApplicable: payable => payable.status === "Draft",
    applyToItem: payable =>
      paymentsService.payable.update(requirePayableId(payable), {
        status: InvoiceStatus.Open
      })
  },
  Delete: {
    cardHeader: "Pending deletion",
    actionName: "Delete payables",
    confirmLabel: "Delete",
    confirmDescription: count =>
      `Are you sure you want to permanently delete ${count.toLocaleString()} draft ${pluralizePayable(
        count
      )}? This action cannot be undone.`,
    confirmDestructive: true,
    isApplicable: payable => payable.status === "Draft",
    applyToItem: payable =>
      paymentsService.payable.delete(requirePayableId(payable))
  }
};
